import {
  useWallets,
  type ConnectedWallet,
  useConnectWallet,
  usePrivy,
  type ConnectWalletModalOptions,
} from '@privy-io/react-auth';
import {
  useWallets as useSolanaWallets,
  type ConnectedStandardSolanaWallet,
} from '@privy-io/react-auth/solana';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useDisconnect, type Connector } from 'wagmi';

import { logger } from '@/lib/logger';

import { AuthContext } from '@/components/Auth/AuthProvider';

import type { CachedWalletAddresses } from '@/utils/cachedWalletAddresses';

const WALLET_RESTORE_TIMEOUT_MS = 3_000;

// Backward-compat alias. The hook now returns the raw Privy wallet types
// directly; older call sites and types referencing MatchaConnectedWallet still
// compile via this passthrough.
export type MatchaConnectedWallet<T extends ConnectedWallet | ConnectedStandardSolanaWallet> = T;

interface UseMatchaWalletsReturn {
  ethereumWallet: ConnectedWallet | undefined;
  solanaWallet: ConnectedStandardSolanaWallet | undefined;
  isConnected: boolean;
  /** Cached addresses used only as restoration hints; null once resolved. */
  restoringWalletAddresses: CachedWalletAddresses | null;
  walletsReady: boolean;
  connect: (options?: ConnectWalletModalOptions) => void;
  disconnectEthereum: () => Promise<void>;
  disconnectSolana: () => Promise<void>;
}

const findMatchingConnector = (
  wallet: ConnectedWallet,
  connectors: readonly Connector[],
): Connector | undefined =>
  connectors.find((connector) => connector.name.toLowerCase() === wallet.meta.name.toLowerCase());

export const useMatchaWallets = (): UseMatchaWalletsReturn => {
  const { ready: privyReady } = usePrivy();
  const { ready: ethereumReady, wallets: ethereumWallets } = useWallets();
  const { ready: solanaReady, wallets: solanaWallets } = useSolanaWallets();
  const { connectWallet } = useConnectWallet();
  const cachedWalletAddresses = useContext(AuthContext)?.cachedWalletAddresses ?? null;
  const { disconnectAsync: wagmiDisconnect, connectors: wagmiConnectors } = useDisconnect();
  const [settled, setSettled] = useState(false);

  const derivedReady =
    privyReady &&
    ethereumReady &&
    solanaReady &&
    ethereumWallets.every((wallet) => findMatchingConnector(wallet, wagmiConnectors) != null);

  useEffect(() => {
    if (settled) return;

    if (derivedReady) {
      setSettled(true);
      return;
    }

    const timeout = setTimeout(() => setSettled(true), WALLET_RESTORE_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [derivedReady, settled]);

  const ethereumWallet = privyReady
    ? ethereumWallets.find((wallet) => findMatchingConnector(wallet, wagmiConnectors))
    : undefined;
  const solanaWallet = privyReady ? solanaWallets[0] : undefined;

  const walletsReady = settled || derivedReady;
  const restoringWalletAddresses = walletsReady ? null : cachedWalletAddresses;

  const connect = useCallback(
    (options?: ConnectWalletModalOptions) => connectWallet(options),
    [connectWallet],
  );

  const disconnectEthereum = useCallback(async () => {
    if (!ethereumWallet) return;
    try {
      const connector = findMatchingConnector(ethereumWallet, wagmiConnectors);
      if (!connector) return;
      await wagmiDisconnect({ connector });
    } catch (error) {
      logger.error('Error disconnecting EVM wallet:', error);
    }
  }, [ethereumWallet, wagmiConnectors, wagmiDisconnect]);

  const disconnectSolana = useCallback(async () => {
    if (!solanaWallet) return;
    try {
      await solanaWallet.disconnect();
    } catch (error) {
      logger.error('Error disconnecting Solana wallet:', error);
    }
  }, [solanaWallet]);

  return {
    ethereumWallet,
    solanaWallet,
    isConnected: !!ethereumWallet || !!solanaWallet,
    restoringWalletAddresses,
    walletsReady,
    connect,
    disconnectEthereum,
    disconnectSolana,
  };
};
